// ============================================================================
// Voice Configuration
// ============================================================================

export const VOICE_IDS = {
  male: 'pNInz6obpgDQGcFmaJgB', // Adam (Vietnamese)
  female: 'EXAVITQu4vr4xnSDxMaL', // Bella (Vietnamese) - Default female
  female2: 'KpzB5RgCRuVkUlZeY6wb', // Female 2 (Vietnamese)
  female3: 'BlZK9tHPU6XXjwOSIiYA', // Female 3 (Vietnamese)
} as const;

export const VOICE_LABELS = {
  male: 'Adam (Male) - pNInz6obpgDQGcFmaJgB',
  female: 'Bella (Female) - EXAVITQu4vr4xnSDxMaL',
  female2: 'Female 2 - KpzB5RgCRuVkUlZeY6wb',
  female3: 'Female 3 - BlZK9tHPU6XXjwOSIiYA',
} as const;

export const TTS_MODELS = {
  eleven_flash_v2_5: 'Eleven Flash v2.5 (Fastest)',
  eleven_turbo_v2_5: 'Eleven Turbo v2.5 (Balanced)',
  eleven_multilingual_v2: 'Eleven Multilingual v2 (Quality)',
  eleven_v3: 'Eleven v3 (Latest)',
} as const;

export const STT_MODELS = {
  scribe_v1: 'Scribe v1',
} as const;

export const ELEVENLABS_CONFIG = {
  baseUrl: import.meta.env.VITE_ELEVENLABS_API_URL || 'https://api.elevenlabs.io/v1',
  language: 'vi',
  languageCode: 'vi', // For TTS API
  voiceSettings: {
    stability: 0.9,
    similarity_boost: 0.75,
    style: 0,
    use_speaker_boost: true,
  },
} as const;

// ============================================================================
// WebSocket Configuration
// ============================================================================

export const WEBSOCKET_CONFIG = {
  defaultUrl: import.meta.env.VITE_DEFAULT_WEBSOCKET_URL || 'https://xora01.x-or.cloud/',
  defaultPath: import.meta.env.VITE_DEFAULT_WEBSOCKET_PATH || '/socket.io',
  reconnectionDelay: 1000,
  reconnectionAttempts: 5,
  transports: ['websocket', 'polling'] as const,
} as const;

// ============================================================================
// WebSocket Events
// ============================================================================

export const WS_EVENTS = {
  // Client → Server
  USER_CONNECT: 'user.connect',
  USER_MESSAGE: 'user.message',
  USER_DISCONNECT: 'user.disconnect',
  CONSOLE_CLEAR: 'console.clear',

  // Server → Client
  AGENT_UPDATE: 'agent.update',
  AGENT_ACTION: 'agent.action',
  CONSOLE_UPDATE: 'console.update',
  AGENT_ERROR: 'agent.error',

  // Socket.IO built-in events
  CONNECT: 'connect',
  DISCONNECT: 'disconnect',
  CONNECT_ERROR: 'connect_error',
  RECONNECT: 'reconnect',
  RECONNECT_ATTEMPT: 'reconnect_attempt',
  RECONNECT_ERROR: 'reconnect_error',
  RECONNECT_FAILED: 'reconnect_failed',
} as const;

// ============================================================================
// LocalStorage Keys
// ============================================================================

export const STORAGE_KEYS = {
  APP_CONFIG: 'agent-console-config',
} as const;

// ============================================================================
// App Configuration
// ============================================================================

export const APP_CONFIG = {
  version: import.meta.env.VITE_APP_VERSION || '1.0.0',
  name: 'Agent Console',
  description: 'BK Fund Venture Day 2025 - X-OR Stack AI Showcase',
} as const;

// ============================================================================
// PTT Configuration
// ============================================================================

export const PTT_CONFIG = {
  hidFilters: [
    { usagePage: 0x0c }, // Consumer devices
    { usagePage: 0x01 }, // Generic desktop
  ],
  audioConstraints: {
    audio: {
      channelCount: 1,
      sampleRate: 16000,
      echoCancellation: true,
      noiseSuppression: true,
    } as MediaStreamConstraints['audio'],
  },
  recordingMimeType: 'audio/webm',
} as const;

// ============================================================================
// Camera Configuration
// ============================================================================

export const CAMERA_CONFIG = {
  videoConstraints: {
    video: {
      width: { ideal: 800 },
      height: { ideal: 600 },
      facingMode: 'user',
    } as MediaStreamConstraints['video'],
  },
  // Capture settings - optimized for LLM vision while minimizing bandwidth
  captureWidth: 640, // Resize to 640x480 for capture (down from 800x600)
  captureHeight: 480,
  captureFormat: 'image/jpeg',
  captureQuality: 0.6, // Lower quality (0.6) still sufficient for LLM, reduces file size by ~60%
} as const;

// ============================================================================
// Performance Targets
// ============================================================================

export const PERFORMANCE_TARGETS = {
  initialLoad: 3000, // ms
  websocketConnect: 1000, // ms
  configLoad: 50, // ms
  pttLatency: 100, // ms
  sttProcessing: 2000, // ms
  ttsGeneration: 1000, // ms
  sectionRender: 100, // ms
  cameraCapture: 50, // ms
} as const;

// ============================================================================
// Agent Status Colors
// ============================================================================

export const AGENT_STATUS_COLORS = {
  idle: 'text-green-500',
  busy: 'text-yellow-500',
  offline: 'text-red-500',
} as const;

export const AGENT_STATUS_ICONS = {
  idle: '🟢',
  busy: '🟡',
  offline: '🔴',
} as const;

// ============================================================================
// Default Configuration
// ============================================================================

export const DEFAULT_CONFIG = {
  username: '',
  password: '',
  authId: '',
  agentName: 'XORA',
  websocketUrl: WEBSOCKET_CONFIG.defaultUrl,
  websocketPath: WEBSOCKET_CONFIG.defaultPath,
  voiceAPIKey: '',
  ttsProvider: 'elevenlabs' as const,
  ttsModel: 'eleven_flash_v2_5' as const,
  voiceGender: 'male' as const,
  voiceId: VOICE_IDS.male,
  sttProvider: 'elevenlabs' as const,
  sttModel: 'scribe_v1' as const,
  pttMode: 'manual' as const, // Default to manual until HID detected
  autoReconnect: true,
  cameraEnabled: true,
  voiceModeEnabled: true, // Enable voice features by default
  theme: 'light' as const,
  version: APP_CONFIG.version,
};
